/**
 * Category Media Section - Shared JS for category-media and category-video
 * Handles: Tab switching, Video pause on tab change, Modal open/close
 */
(() => {
  function initCategoryMediaSection(section) {
    if (!section || section.dataset.initialized === 'true') return;
    section.dataset.initialized = 'true';

    const tabs = Array.from(section.querySelectorAll('[data-category-tab]'));
    const panels = Array.from(section.querySelectorAll('[data-category-panel]'));

    function activateTab(tab, moveFocus = false) {
      const key = tab.getAttribute('data-category-tab');
      tabs.forEach((t) => {
        const active = t === tab;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-selected', active ? 'true' : 'false');
        t.tabIndex = active ? 0 : -1;
      });
      panels.forEach((p) => {
        const match = p.getAttribute('data-category-panel') === key;
        p.hidden = !match;
        if (!match) pauseVideosInElement(p);
      });
      if (moveFocus) tab.focus();
      tab.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }

    // Tab switching
    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activateTab(tab));
      tab.addEventListener('keydown', (event) => {
        let nextIndex = index;
        if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
        else if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
        else if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = tabs.length - 1;
        else return;
        event.preventDefault();
        activateTab(tabs[nextIndex], true);
      });
    });

    // Video Modal
    const modal = section.querySelector('[data-video-modal]');
    const modalVideo = section.querySelector('[data-modal-video]');
    const modalClose = section.querySelector('[data-modal-close]');
    let modalTrigger = null;

    function openModal(html, trigger) {
      if (!modal || !modalVideo) return;
      modalTrigger = trigger;
      modalVideo.innerHTML = html;
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      modalClose?.focus();
      const video = modalVideo.querySelector('video');
      if (video) {
        video.play().catch((e) => console.warn('Video autoplay failed:', e));
      }
    }

    function closeModal() {
      if (!modal || !modalVideo) return;
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      pauseVideosInElement(modalVideo);
      setTimeout(() => { modalVideo.innerHTML = ''; }, 300);
      modalTrigger?.focus();
    }

    modalClose?.addEventListener('click', closeModal);
    modal?.addEventListener('click', (e) => {
      if (e.target === modal) closeModal();
    });

    // ESC key to close modal (only bind once per section)
    section._escHandler = (e) => {
      if (!modal?.classList.contains('is-open')) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        closeModal();
      } else if (e.key === 'Tab') {
        const focusable = Array.from(modal.querySelectorAll('button:enabled, video[controls], iframe, [tabindex]:not([tabindex="-1"])'));
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', section._escHandler);

    // Play button click - open modal
    section.querySelectorAll('[data-media-play], [data-video-play]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const type = btn.getAttribute('data-video-type');
        let html = '';
        if (type === 'file') {
          const src = btn.getAttribute('data-video-src');
          html = `<video src="${src}" controls playsinline preload="metadata" aria-label="Video player"></video>`;
        } else if (type === 'youtube') {
          const id = btn.getAttribute('data-video-id');
          html = `<iframe title="YouTube video player" src="https://www.youtube.com/embed/${id}?autoplay=1&rel=0&modestbranding=1&enablejsapi=1" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`;
        } else if (type === 'vimeo') {
          const id = btn.getAttribute('data-video-id');
          html = `<iframe title="Vimeo video player" src="https://player.vimeo.com/video/${id}?autoplay=1" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>`;
        }
        if (html) openModal(html, btn);
      });
    });
  }

  function pauseVideosInElement(el) {
    if (!el) return;
    el.querySelectorAll('video').forEach((v) => {
      try { v.pause(); } catch (e) {}
    });
    el.querySelectorAll('iframe').forEach((iframe) => {
      if (iframe.src.includes('youtube.com/embed')) {
        try {
          iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        } catch (e) {}
      }
    });
  }

  // Initialize on DOM ready
  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.section-category-media[data-section-id]').forEach(initCategoryMediaSection);
  });

  // Expose for dynamic initialization
  window.initCategoryMediaSection = initCategoryMediaSection;
})();
